import { BACKEND_URL } from '../config/backendUrl';
import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { templatesApi, packetsApi, getErrorMessage } from '../services/api';
import { parseCcEmails } from '../utils/parseCcEmails';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { templateTypeLabel } from '../utils/templateType';
import TemplateBasicsFields from '../components/templates/TemplateBasicsFields';
import PDF_ICON from '../assets/file-type-pdf.png';
import TXT_ICON from '../assets/file-type-txt.png';
import { 
  Plus, 
  FileText, 
  Trash2, 
  Upload, 
  RefreshCw,
  MoreVertical,
  Eye,
  User,
  Building2,
  Package,
  Files,
  Edit2,
  Briefcase,
  Copy,
  ShieldCheck,
  ArrowUp,
  ArrowDown
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';

// Offered in the "New template" dialog until (or unless) Bullhorn supplies its own list.
const DEFAULT_FILE_TYPES = [
  'CV', 'Resume', 'Application', 'Contract', 'Reference',
  'Right to Work', 'ID Document', 'Tax Form', 'Training Certificate',
  'Background Check', 'Medical', 'Other'
];

// Bullhorn entity colors (from Bullhorn official brand guidelines)
const ENTITY_COLORS = {
  candidate: { bg: 'bg-[#4CAF50]', text: 'text-white', label: 'Candidate', lightBg: 'bg-[#4CAF50]/10', iconColor: 'text-[#4CAF50]' },
  contact: { bg: 'bg-[#F9A825]', text: 'text-white', label: 'Contact', lightBg: 'bg-[#F9A825]/10', iconColor: 'text-[#F9A825]' },
  placement: { bg: 'bg-[#1a4a5e]', text: 'text-white', label: 'Placement', lightBg: 'bg-[#1a4a5e]/10', iconColor: 'text-[#1a4a5e]' },
};

export default function TemplatesPage() {
  const [activeTab, setActiveTab] = useState('templates');
  const [templates, setTemplates] = useState([]);
  const [packets, setPackets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isPacketOpen, setIsPacketOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newTemplate, setNewTemplate] = useState({ name: '', description: '', entity_type: 'candidate', bullhorn_file_type: '' });
  const [selectedFile, setSelectedFile] = useState(null);
  // "New Text Template": the basics, plus an optional Word file to start the text from.
  const [isTextOpen, setIsTextOpen] = useState(false);
  const [newText, setNewText] = useState({ name: '', description: '', entity_type: 'candidate', bullhorn_file_type: '' });
  const [wordFile, setWordFile] = useState(null);
  const [startingText, setStartingText] = useState(false);
  const [editingPacket, setEditingPacket] = useState(null);
  const [bullhornFileTypes, setBullhornFileTypes] = useState(DEFAULT_FILE_TYPES);
  const [newPacket, setNewPacket] = useState({
    name: '',
    description: '',
    template_ids: [],
    entity_type: 'candidate',
    completion_message: '',
    email_subject: '',
    email_message: '',
    verification_steps: []
    // notification_cc_emails is deliberately absent until the recruiter
    // opts into a custom list (see packetCcInput below) — undefined means
    // "inherit the workspace default", an array is an explicit override.
  });
  // Raw textarea text for the packet-level completion-notification CC
  // override, parsed into newPacket.notification_cc_emails on submit —
  // same pattern as the Template Editor's own override.
  const [packetCcInput, setPacketCcInput] = useState('');
  const navigate = useNavigate();
  const { getTenantPath, tenant } = useAuth();

  useEffect(() => {
    loadData();
    // loadData has no dependencies that change after mount; safe to fire once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    const [templatesResult, packetsResult] = await Promise.allSettled([
      // This page doesn't show time estimates, so skip the lookup behind them.
      templatesApi.list({ estimates: 0 }),
      packetsApi.list()
    ]);

    if (templatesResult.status === 'fulfilled') {
      setTemplates(templatesResult.value.data);
    } else {
      toast.error('Failed to load templates');
    }

    if (packetsResult.status === 'fulfilled') {
      setPackets(packetsResult.value.data);
    } else {
      toast.error('Failed to load packets');
    }

    // Everything the page shows is here — render it. The Bullhorn file types only fill a dropdown in
    // the "New template" dialog, so they load in the background and must never hold the page up
    // (Bullhorn can take a long time to answer, or not answer at all).
    setLoading(false);
    loadBullhornFileTypes();
  };

  const loadBullhornFileTypes = async () => {
    // Workspaces that have never set up Bullhorn have nothing to ask.
    if (tenant?.bullhorn_config?.configured === false) {
      setBullhornFileTypes(DEFAULT_FILE_TYPES);
      return;
    }
    try {
      const fileTypesRes = await axios.get(`${BACKEND_URL}/api/bullhorn/file-types`, { timeout: 20000 });
      const types = (fileTypesRes.data.file_types || []).map(t => typeof t === 'object' ? t.value : t);
      setBullhornFileTypes(types.length ? types : DEFAULT_FILE_TYPES);
    } catch (e) {
      setBullhornFileTypes(DEFAULT_FILE_TYPES);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newTemplate.name.trim()) {
      toast.error('Please enter a template name');
      return;
    }
    
    setCreating(true);
    try {
      const formData = new FormData();
      formData.append('name', newTemplate.name);
      formData.append('description', newTemplate.description || '');
      formData.append('entity_type', newTemplate.entity_type);
      formData.append('bullhorn_file_type', newTemplate.bullhorn_file_type || '');
      formData.append('fields', JSON.stringify([]));
      if (selectedFile) {
        formData.append('file', selectedFile);
      }

      await templatesApi.create(formData);
      toast.success('Template created successfully');
      setIsCreateOpen(false);
      setNewTemplate({ name: '', description: '', entity_type: 'candidate', bullhorn_file_type: '' });
      setSelectedFile(null);
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create template'));
    } finally {
      setCreating(false);
    }
  };

  const chooseWordFile = (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.docx')) {
      toast.error('Please choose a Word .docx file (save older .doc files as .docx first)');
      return;
    }
    setWordFile(file);
  };

  // Nothing is saved yet: the text editor opens with these answers (and the converted Word text,
  // if a file was chosen) and the template is created when it is saved there.
  const handleStartText = async (e) => {
    e.preventDefault();
    if (!newText.name.trim()) {
      toast.error('Please enter a template name');
      return;
    }
    setStartingText(true);
    try {
      let html = '';
      if (wordFile) {
        const { data } = await templatesApi.convertDocx(wordFile);
        html = data.html_content || '';
      }
      setIsTextOpen(false);
      navigate(getTenantPath('/templates/contract/new'), {
        state: {
          draft: {
            name: newText.name.trim(),
            description: newText.description,
            entity_type: newText.entity_type,
            bullhorn_file_type: newText.bullhorn_file_type,
            html,
          },
        },
      });
      setNewText({ name: '', description: '', entity_type: 'candidate', bullhorn_file_type: '' });
      setWordFile(null);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Could not convert that file'));
    } finally {
      setStartingText(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"?`)) {
      return;
    }
    try {
      await templatesApi.delete(id);
      toast.success('Template deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const handleDuplicate = async (id, name) => {
    try {
      await templatesApi.duplicate(id);
      toast.success(`Duplicated "${name}"`);
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to duplicate template'));
    }
  };

  // Packet handlers
  const handleCreatePacket = async (e) => {
    e.preventDefault();
    if (!newPacket.name.trim()) {
      toast.error('Please enter a packet name');
      return;
    }
    if (newPacket.template_ids.length === 0) {
      toast.error('Please select at least one document');
      return;
    }
    // Parsed up front so a bad email aborts before anything is saved,
    // matching the required-field checks above.
    let payload = newPacket;
    if (newPacket.notification_cc_emails !== undefined) {
      try {
        payload = { ...newPacket, notification_cc_emails: parseCcEmails(packetCcInput) };
      } catch (ccError) {
        toast.error(ccError.message);
        return;
      }
    }

    setCreating(true);
    try {
      if (editingPacket) {
        await packetsApi.update(editingPacket.id, payload);
        toast.success('Packet updated successfully');
      } else {
        await packetsApi.create(payload);
        toast.success('Packet created successfully');
      }
      setIsPacketOpen(false);
      setEditingPacket(null);
      setNewPacket({
        name: '',
        description: '',
        template_ids: [],
        entity_type: 'candidate',
        completion_message: '',
        email_subject: '',
        email_message: '',
        verification_steps: []
      });
      setPacketCcInput('');
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save packet'));
    } finally {
      setCreating(false);
    }
  };

  const handleDeletePacket = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"?`)) {
      return;
    }
    try {
      await packetsApi.delete(id);
      toast.success('Packet deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete packet');
    }
  };

  const handleEditPacket = (packet) => {
    setEditingPacket(packet);
    setNewPacket({
      name: packet.name,
      description: packet.description || '',
      template_ids: packet.template_ids || [],
      entity_type: packet.entity_type || 'candidate',
      completion_message: packet.completion_message || '',
      email_subject: packet.email_subject || '',
      email_message: packet.email_message || '',
      verification_steps: packet.verification_steps || [],
      // undefined (key absent) = inherit the workspace CC list; an array
      // (including []) means this packet already has its own override.
      ...(packet.notification_cc_emails != null ? { notification_cc_emails: packet.notification_cc_emails } : {}),
    });
    setPacketCcInput((packet.notification_cc_emails || []).join(', '));
    setIsPacketOpen(true);
  };

  const toggleTemplateInPacket = (templateId) => {
    setNewPacket(prev => ({
      ...prev,
      template_ids: prev.template_ids.includes(templateId)
        ? prev.template_ids.filter(id => id !== templateId)
        : [...prev.template_ids, templateId]
    }));
  };

  // template_ids is the packet's order — the backend stores each template's
  // position from its index, and the candidate sees the documents in that order.
  const moveTemplateInPacket = (templateId, delta) => {
    setNewPacket(prev => {
      const from = prev.template_ids.indexOf(templateId);
      const to = from + delta;
      if (from < 0 || to < 0 || to >= prev.template_ids.length) return prev;
      const ids = [...prev.template_ids];
      [ids[from], ids[to]] = [ids[to], ids[from]];
      return { ...prev, template_ids: ids };
    });
  };

  const hasTrustIdStep = newPacket.verification_steps.some(
    (s) => s.provider === 'trustid' && s.check_type === 'right_to_work'
  );

  const toggleTrustIdStep = () => {
    setNewPacket(prev => ({
      ...prev,
      verification_steps: hasTrustIdStep
        ? []
        : [{ provider: 'trustid', check_type: 'right_to_work' }]
    }));
  };

  // Filter templates by entity type for packet creation.
  // Placement packets are special — they can bundle BOTH candidate-mapped
  // and contact-mapped templates so the system can fan-out documents to
  // the right party at send time.
  const filteredTemplates = newPacket.entity_type === 'placement'
    ? templates.filter((t) => ['candidate', 'contact', 'placement'].includes(t.entity_type || 'candidate'))
    : templates.filter((t) => (t.entity_type || 'candidate') === newPacket.entity_type);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="templates-page">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="font-heading text-3xl font-bold text-foreground">Templates & Packets</h1>
            <p className="text-muted-foreground font-body mt-1">
              Manage documents and group them into packets
            </p>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <div className="flex items-center justify-between">
            <TabsList className="grid grid-cols-2 w-fit">
              <TabsTrigger value="templates" className="gap-2" data-testid="templates-tab">
                <FileText className="w-4 h-4" />
                Templates ({templates.length})
              </TabsTrigger>
              <TabsTrigger value="packets" className="gap-2" data-testid="packets-tab">
                <Package className="w-4 h-4" />
                Packets ({packets.length})
              </TabsTrigger>
            </TabsList>

            {/* Context-aware Create Button */}
            {activeTab === 'templates' ? (
              <div className="flex items-center gap-2">
              <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
                <DialogTrigger asChild>
                  <Button className="btn-primary" data-testid="create-template-button">
                    <Plus className="w-4 h-4 mr-2" />
                    New PDF Template
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[548px]">
                  <DialogHeader>
                    <DialogTitle className="font-heading">Create PDF Template</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreate} className="space-y-4 mt-4" data-testid="create-template-form">
                    <TemplateBasicsFields value={newTemplate} onChange={setNewTemplate} fileTypes={bullhornFileTypes} />

                    <div className="space-y-2">
                      <Label>Document File (PDF)</Label>
                      <div 
                        className="border-2 border-dashed border-border rounded-lg p-6 text-center hover:border-primary/50 transition-colors duration-200"
                        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); e.currentTarget.classList.add('border-primary', 'bg-primary/5'); }}
                        onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); e.currentTarget.classList.remove('border-primary', 'bg-primary/5'); }}
                        onDrop={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          e.currentTarget.classList.remove('border-primary', 'bg-primary/5');
                          const f = e.dataTransfer.files[0];
                          if (f && f.name.toLowerCase().endsWith('.pdf')) {
                            setSelectedFile(f);
                          } else {
                            toast.error('Please drop a PDF file');
                          }
                        }}
                      >
                        <input
                          type="file"
                          accept=".pdf"
                          onChange={(e) => setSelectedFile(e.target.files[0])}
                          className="hidden"
                          id="file-upload"
                          data-testid="template-file-input"
                        />
                        <label htmlFor="file-upload" className="cursor-pointer">
                          {selectedFile ? (
                            <div className="flex items-center justify-center gap-2 text-primary">
                              <FileText className="w-5 h-5" />
                              <span className="font-medium">{selectedFile.name}</span>
                            </div>
                          ) : (
                            <>
                              <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                              <p className="text-sm text-muted-foreground">
                                Click to upload or drag and drop
                              </p>
                              <p className="text-xs text-muted-foreground mt-1">
                                PDF only (max 10MB)
                              </p>
                            </>
                          )}
                        </label>
                      </div>
                    </div>
                    <div className="flex justify-end gap-3 pt-4">
                      <Button 
                        type="button" 
                        variant="outline" 
                        onClick={() => setIsCreateOpen(false)}
                      >
                        Cancel
                      </Button>
                      <Button 
                        type="submit" 
                        className="btn-primary"
                        disabled={creating}
                        data-testid="submit-template-button"
                      >
                        {creating ? (
                          <>
                            <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                            Creating...
                          </>
                        ) : (
                          'Create PDF Template'
                        )}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
              <Dialog open={isTextOpen} onOpenChange={setIsTextOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" data-testid="create-contract-button">
                    <FileText className="w-4 h-4 mr-2" />
                    New Text Template
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[548px] max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="font-heading">Create Text Template</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleStartText} className="space-y-4 mt-4" data-testid="create-text-template-form">
                    <TemplateBasicsFields value={newText} onChange={setNewText} fileTypes={bullhornFileTypes} />

                    <div className="space-y-2">
                      <Label>Template text (optional)</Label>
                      <div
                        className="border-2 border-dashed border-border rounded-lg p-6 text-center hover:border-primary/50 transition-colors duration-200"
                        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); e.currentTarget.classList.add('border-primary', 'bg-primary/5'); }}
                        onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); e.currentTarget.classList.remove('border-primary', 'bg-primary/5'); }}
                        onDrop={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          e.currentTarget.classList.remove('border-primary', 'bg-primary/5');
                          chooseWordFile(e.dataTransfer.files[0]);
                        }}
                      >
                        <input
                          type="file"
                          accept=".docx"
                          onChange={(e) => { chooseWordFile(e.target.files[0]); e.target.value = ''; }}
                          className="hidden"
                          id="word-file-upload"
                          data-testid="word-file-input"
                        />
                        <label htmlFor="word-file-upload" className="cursor-pointer">
                          {wordFile ? (
                            <div className="flex items-center justify-center gap-2 text-primary">
                              <FileText className="w-5 h-5" />
                              <span className="font-medium">{wordFile.name}</span>
                            </div>
                          ) : (
                            <>
                              <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                              <p className="text-sm font-medium">Convert File (Word docx)</p>
                              <p className="text-xs text-muted-foreground mt-1">
                                Click to choose or drag and drop (max 10MB)
                              </p>
                            </>
                          )}
                        </label>
                        {wordFile && (
                          <button
                            type="button"
                            onClick={() => setWordFile(null)}
                            className="mt-2 text-xs text-muted-foreground underline"
                            data-testid="word-file-remove"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Turns a Word document into text you can edit. Formatting is simplified, so check it in the editor.
                        Or skip this and paste the text straight into the editor on the next screen.
                      </p>
                    </div>

                    <div className="flex justify-end gap-3 pt-4">
                      <Button type="button" variant="outline" onClick={() => setIsTextOpen(false)}>
                        Cancel
                      </Button>
                      <Button type="submit" className="btn-primary" disabled={startingText} data-testid="submit-text-template-button">
                        {startingText ? (
                          <>
                            <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                            {wordFile ? 'Converting...' : 'Opening...'}
                          </>
                        ) : (
                          'Continue to editor'
                        )}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
              </div>
            ) : (
              <Dialog open={isPacketOpen} onOpenChange={(open) => {
                setIsPacketOpen(open);
                if (!open) {
                  setEditingPacket(null);
                  setNewPacket({
                    name: '',
                    description: '',
                    template_ids: [],
                    entity_type: 'candidate',
                    completion_message: '',
                    email_subject: '',
                    email_message: '',
                    verification_steps: []
                  });
                  setPacketCcInput('');
                }
              }}>
                <DialogTrigger asChild>
                  <Button className="btn-primary" data-testid="create-packet-button">
                    <Plus className="w-4 h-4 mr-2" />
                    New Packet
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="font-heading">
                      {editingPacket ? 'Edit Packet' : 'Create Packet'}
                    </DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreatePacket} className="space-y-4 mt-4" data-testid="create-packet-form">
                    {/* Packet Name */}
                    <div className="space-y-2">
                      <Label htmlFor="packet-name">Packet Name</Label>
                      <Input
                        id="packet-name"
                        value={newPacket.name}
                        onChange={(e) => setNewPacket({ ...newPacket, name: e.target.value })}
                        placeholder="e.g., New Hire Onboarding"
                        className="input-focus"
                        data-testid="packet-name-input"
                      />
                    </div>

                    {/* Description */}
                    <div className="space-y-2">
                      <Label htmlFor="packet-description">Description (optional)</Label>
                      <Textarea
                        id="packet-description"
                        value={newPacket.description}
                        onChange={(e) => setNewPacket({ ...newPacket, description: e.target.value })}
                        placeholder="Brief description of this packet"
                        className="input-focus"
                        rows={2}
                      />
                    </div>

                    {/* Entity Type */}
                    <div className="space-y-2">
                      <Label>Packet Type</Label>
                      <div className="grid grid-cols-3 gap-3">
                        <button
                          type="button"
                          onClick={() => setNewPacket({ ...newPacket, entity_type: 'candidate', template_ids: [] })}
                          className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-all ${
                            newPacket.entity_type === 'candidate'
                              ? 'border-[#4CAF50] bg-[#4CAF50]/10'
                              : 'border-border hover:border-[#4CAF50]/50'
                          }`}
                        >
                          <User className={`w-5 h-5 ${newPacket.entity_type === 'candidate' ? 'text-[#4CAF50]' : 'text-muted-foreground'}`} />
                          <span className="font-medium">Candidate</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setNewPacket({ ...newPacket, entity_type: 'contact', template_ids: [], verification_steps: [] })}
                          className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-all ${
                            newPacket.entity_type === 'contact'
                              ? 'border-[#F9A825] bg-[#F9A825]/10'
                              : 'border-border hover:border-[#F9A825]/50'
                          }`}
                        >
                          <Building2 className={`w-5 h-5 ${newPacket.entity_type === 'contact' ? 'text-[#F9A825]' : 'text-muted-foreground'}`} />
                          <span className="font-medium">Contact</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setNewPacket({ ...newPacket, entity_type: 'placement', template_ids: [] })}
                          className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-all ${
                            newPacket.entity_type === 'placement'
                              ? 'border-[#1a4a5e] bg-[#1a4a5e]/10'
                              : 'border-border hover:border-[#1a4a5e]/50'
                          }`}
                        >
                          <Briefcase className={`w-5 h-5 ${newPacket.entity_type === 'placement' ? 'text-[#1a4a5e]' : 'text-muted-foreground'}`} />
                          <span className="font-medium">Placement</span>
                        </button>
                      </div>
                    </div>

                    {/* Document Selection */}
                    <div className="space-y-2">
                      <Label>Select Documents ({newPacket.template_ids.length} selected)</Label>
                      <div className="border rounded-lg max-h-48 overflow-y-auto divide-y">
                        {filteredTemplates.length > 0 ? (
                          filteredTemplates.map(template => {
                            const isSelected = newPacket.template_ids.includes(template.id);
                            return (
                              <div 
                                key={template.id}
                                className={`flex items-center gap-3 p-3 hover:bg-muted/50 cursor-pointer transition-colors ${
                                  isSelected ? 'bg-primary/5' : ''
                                }`}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  toggleTemplateInPacket(template.id);
                                }}
                              >
                                <div 
                                  className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                                    isSelected ? 'bg-primary border-primary' : 'border-muted-foreground'
                                  }`}
                                >
                                  {isSelected && (
                                    <svg className="w-3 h-3 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                      <polyline points="20 6 9 17 4 12" />
                                    </svg>
                                  )}
                                </div>
                                <FileText className="w-4 h-4 text-muted-foreground" />
                                <div className="flex-1">
                                  <p className="font-medium text-sm">{template.name}</p>
                                  <p className="text-xs text-muted-foreground">{template.fields?.length || 0} fields</p>
                                </div>
                                {/* Routing badge — only visible on placement
                                    packets, where templates of different entity
                                    types live side by side. */}
                                {newPacket.entity_type === 'placement' && (
                                  <span
                                    className={`text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded-full border ${
                                      (template.entity_type || 'candidate') === 'contact'
                                        ? 'bg-amber-50 border-amber-200 text-amber-800'
                                        : 'bg-emerald-50 border-emerald-200 text-emerald-800'
                                    }`}
                                  >
                                    {(template.entity_type || 'candidate') === 'contact' ? 'Contact' : 'Candidate'}
                                  </span>
                                )}
                              </div>
                            );
                          })
                        ) : (
                          <div className="p-4 text-center text-muted-foreground text-sm">
                            No {newPacket.entity_type} templates available
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Order the selected documents are presented in */}
                    {newPacket.template_ids.length > 1 && (
                      <div className="space-y-2" data-testid="packet-document-order">
                        <Label>Document order</Label>
                        <p className="text-xs text-muted-foreground">
                          The order the recipient sees these documents in, top first.
                        </p>
                        <ol className="border rounded-lg divide-y">
                          {newPacket.template_ids.map((id, index) => (
                            <li key={id} className="flex items-center gap-2 px-3 py-1.5" data-testid={`packet-order-row-${index}`}>
                              <span className="w-5 text-xs text-muted-foreground tabular-nums">{index + 1}.</span>
                              <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                              <span className="flex-1 text-sm truncate">
                                {templates.find(t => t.id === id)?.name ?? 'Unknown document'}
                              </span>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7"
                                disabled={index === 0}
                                onClick={() => moveTemplateInPacket(id, -1)}
                                aria-label="Move up"
                                data-testid={`packet-order-up-${index}`}
                              >
                                <ArrowUp className="w-4 h-4" />
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7"
                                disabled={index === newPacket.template_ids.length - 1}
                                onClick={() => moveTemplateInPacket(id, 1)}
                                aria-label="Move down"
                                data-testid={`packet-order-down-${index}`}
                              >
                                <ArrowDown className="w-4 h-4" />
                              </Button>
                            </li>
                          ))}
                        </ol>
                      </div>
                    )}

                    {/* TrustID Right to Work Verification Step */}
                    <div className="space-y-2 pt-2 border-t">
                      <div
                        className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-all ${
                          newPacket.entity_type === 'contact'
                            ? 'border-border opacity-50 cursor-not-allowed'
                            : hasTrustIdStep
                              ? 'border-emerald-500 bg-emerald-50 cursor-pointer'
                              : 'border-border hover:border-emerald-300 cursor-pointer'
                        }`}
                        onClick={() => {
                          if (newPacket.entity_type !== 'contact') toggleTrustIdStep();
                        }}
                        data-testid="packet-trustid-step-toggle"
                      >
                        <Checkbox
                          checked={hasTrustIdStep}
                          disabled={newPacket.entity_type === 'contact'}
                          onCheckedChange={() => toggleTrustIdStep()}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <ShieldCheck className={`w-5 h-5 ${hasTrustIdStep ? 'text-emerald-600' : 'text-muted-foreground'}`} />
                        <div className="flex-1">
                          <p className="font-medium text-sm">Include TrustID Right to Work check</p>
                          <p className="text-xs text-muted-foreground">
                            {newPacket.entity_type === 'contact'
                              ? 'Not available for contact packets'
                              : "Candidate completes an ID scan + liveness check via TrustID; a pass writes back to Bullhorn as a Credential"}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Email Settings */}
                    <div className="space-y-3 pt-2 border-t">
                      <Label className="text-sm font-medium">Email Notification</Label>
                      <div className="space-y-2">
                        <Input
                          value={newPacket.email_subject}
                          onChange={(e) => setNewPacket({ ...newPacket, email_subject: e.target.value })}
                          placeholder="Custom email subject (optional)"
                          className="input-focus text-sm"
                        />
                        <Textarea
                          value={newPacket.email_message}
                          onChange={(e) => setNewPacket({ ...newPacket, email_message: e.target.value })}
                          placeholder="Custom email message (optional) - The recipient will be notified that they have documents to complete"
                          className="input-focus text-sm"
                          rows={2}
                        />
                      </div>
                    </div>

                    {/* Completion Message */}
                    <div className="space-y-2">
                      <Label>Completion Message (optional)</Label>
                      <Textarea
                        value={newPacket.completion_message}
                        onChange={(e) => setNewPacket({ ...newPacket, completion_message: e.target.value })}
                        placeholder="Message shown to the recipient after completing all documents"
                        className="input-focus text-sm"
                        rows={2}
                      />
                    </div>

                    {/* Completion-notification CC override — same field as
                        Settings → Notifications → Additional recipients and
                        the per-template override, but for every document
                        sent through this packet. A document's own template
                        override wins over this if both are set. */}
                    <div className="space-y-2 border-t pt-4">
                      <Label className="text-sm font-medium">Completion notification recipients (CC)</Label>
                      <p className="text-xs text-muted-foreground">
                        Override the workspace's default CC list for documents sent through this packet.
                      </p>
                      <Select
                        value={newPacket.notification_cc_emails === undefined ? 'inherit' : 'custom'}
                        onValueChange={(v) => {
                          if (v === 'inherit') {
                            const { notification_cc_emails, ...rest } = newPacket;
                            setNewPacket(rest);
                            setPacketCcInput('');
                          } else {
                            setNewPacket({ ...newPacket, notification_cc_emails: newPacket.notification_cc_emails ?? [] });
                          }
                        }}
                      >
                        <SelectTrigger className="h-9 text-sm" data-testid="packet-notification-cc-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="inherit">Use workspace default</SelectItem>
                          <SelectItem value="custom">Custom recipients</SelectItem>
                        </SelectContent>
                      </Select>
                      {newPacket.notification_cc_emails !== undefined && (
                        <>
                          <Textarea
                            value={packetCcInput}
                            onChange={(e) => setPacketCcInput(e.target.value)}
                            placeholder="hr@yourcompany.com, compliance@yourcompany.com"
                            className="input-focus text-sm"
                            rows={2}
                            data-testid="packet-notification-cc-input"
                          />
                          <p className="text-[11px] text-muted-foreground">
                            Comma-, semicolon-, or newline-separated. Leave blank to CC no one for this packet.
                          </p>
                        </>
                      )}
                    </div>

                    <div className="flex justify-end gap-3 pt-4">
                      <Button 
                        type="button" 
                        variant="outline" 
                        onClick={() => {
                          setIsPacketOpen(false);
                          setEditingPacket(null);
                        }}
                      >
                        Cancel
                      </Button>
                      <Button 
                        type="submit" 
                        className="btn-primary"
                        disabled={creating}
                        data-testid="submit-packet-button"
                      >
                        {creating ? (
                          <>
                            <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                            Saving...
                          </>
                        ) : (
                          editingPacket ? 'Update Packet' : 'Create Packet'
                        )}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>

          {/* Templates Tab Content */}
          <TabsContent value="templates" className="mt-6">
            {templates.length > 0 ? (
              (() => {
                // Group templates by entity_type and sort each group
                // alphabetically. Renders as three side-by-side columns
                // (Candidate · Placement · Contact) so recruiters can
                // scan the doc set by recipient type instantly. Empty
                // columns still render a header + "No documents" hint
                // so the layout doesn't collapse asymmetrically.
                const groups = {
                  candidate: [],
                  placement: [],
                  contact: [],
                };
                for (const t of templates) {
                  const k = (t.entity_type || 'candidate');
                  (groups[k] || groups.candidate).push(t);
                }
                for (const k of Object.keys(groups)) {
                  groups[k].sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));
                }
                const columns = [
                  { key: 'candidate', label: 'Candidate', items: groups.candidate },
                  { key: 'placement', label: 'Placement', items: groups.placement },
                  { key: 'contact', label: 'Contact', items: groups.contact },
                ];
                return (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {columns.map((col) => {
                      const ec = ENTITY_COLORS[col.key];
                      return (
                        <div key={col.key} data-testid={`templates-col-${col.key}`}>
                          <div className="flex items-center justify-between mb-2 px-1">
                            <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                              <span className={`inline-block w-2 h-2 rounded-full ${ec.bg}`} />
                              {col.label} Documents
                              <span className="text-xs text-muted-foreground font-normal">({col.items.length})</span>
                            </h3>
                          </div>
                          {col.items.length === 0 ? (
                            <div className="rounded-lg border border-dashed border-slate-200 p-4 text-center text-xs text-muted-foreground">
                              No {col.label.toLowerCase()} documents
                            </div>
                          ) : (
                            <div className="space-y-2">
                              {col.items.map((template) => (
                                <div
                                  key={template.id}
                                  className="group neumorphic rounded-lg px-3 py-2.5 hover:shadow-md transition-shadow"
                                  data-testid={`template-card-${template.id}`}
                                >
                                  <div className="flex items-stretch gap-2">
                                    <div className="flex-1 min-w-0">
                                      {/* Top row — title (truncate) + info badge, all on one line */}
                                      <div className="flex items-center gap-2">
                                        <div className={`w-7 h-7 rounded-md ${ec.lightBg} flex items-center justify-center flex-shrink-0`}>
                                          {col.key === 'contact' ? (
                                            <Building2 className={`w-3.5 h-3.5 ${ec.iconColor}`} />
                                          ) : (
                                            <User className={`w-3.5 h-3.5 ${ec.iconColor}`} />
                                          )}
                                        </div>
                                        <h4 className="font-medium text-sm truncate flex-1 min-w-0" title={template.name}>
                                          {template.name}
                                        </h4>
                                        {template.is_informational && (
                                          <span
                                            className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded-full bg-sky-100 text-sky-700 border border-sky-200 flex-shrink-0 whitespace-nowrap"
                                            title="Read-only · no required fields · skips Bullhorn write-back"
                                          >
                                            <Eye className="w-2.5 h-2.5" />
                                            Info
                                          </span>
                                        )}
                                      </div>
                                      {/* Meta row — single line, no wrap */}
                                      <div className="flex items-center gap-2 mt-1 text-[11px] text-muted-foreground pl-9 whitespace-nowrap overflow-hidden">
                                        <span>{template.fields?.length || 0} fields</span>
                                        <span className="text-slate-300">·</span>
                                        <span>{format(new Date(template.created_at), 'MMM d, yyyy')}</span>
                                      </div>
                                    </div>
                                    {/* File-type icon: spans both lines of the card (title row and meta row) */}
                                    <img
                                      src={template.source_format === 'html' ? TXT_ICON : PDF_ICON}
                                      alt={`${templateTypeLabel(template)} template`}
                                      title={template.source_format === 'html' ? 'Text template' : 'PDF template'}
                                      className="w-7 h-7 flex-shrink-0 self-center object-contain"
                                      data-testid="template-type-icon"
                                    />
                                    <div className="h-7 flex items-center flex-shrink-0">
                                      <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                          <Button variant="ghost" size="icon" className="h-6 w-6 flex-shrink-0">
                                            <MoreVertical className="w-3.5 h-3.5" />
                                          </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end">
                                          <DropdownMenuItem onClick={() => navigate(getTenantPath(template.source_format === 'html' ? `/templates/contract/${template.id}` : `/templates/${template.id}`))}>
                                            <Eye className="w-4 h-4 mr-2" />
                                            View & Edit
                                          </DropdownMenuItem>
                                          <DropdownMenuItem
                                            onClick={() => handleDuplicate(template.id, template.name)}
                                            data-testid={`duplicate-template-${template.id}`}
                                          >
                                            <Copy className="w-4 h-4 mr-2" />
                                            Duplicate
                                          </DropdownMenuItem>
                                          <DropdownMenuItem
                                            onClick={() => handleDelete(template.id, template.name)}
                                            className="text-red-600"
                                          >
                                            <Trash2 className="w-4 h-4 mr-2" />
                                            Delete
                                          </DropdownMenuItem>
                                        </DropdownMenuContent>
                                      </DropdownMenu>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })()
            ) : (
              <div className="neumorphic rounded-xl p-12 text-center">
                <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                <h3 className="font-heading font-semibold text-lg mb-2">No templates yet</h3>
                <p className="text-muted-foreground font-body mb-6 max-w-md mx-auto">
                  Create your first template to start sending documents
                </p>
                <Button 
                  onClick={() => setIsCreateOpen(true)}
                  className="btn-primary"
                  data-testid="create-first-template"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create PDF Template
                </Button>
              </div>
            )}
          </TabsContent>

          {/* Packets Tab Content */}
          <TabsContent value="packets" className="mt-6">
            {packets.length > 0 ? (
              (() => {
                const groups = { candidate: [], placement: [], contact: [] };
                for (const p of packets) {
                  const k = (p.entity_type || 'candidate');
                  (groups[k] || groups.candidate).push(p);
                }
                for (const k of Object.keys(groups)) {
                  groups[k].sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));
                }
                const columns = [
                  { key: 'candidate', label: 'Candidate', items: groups.candidate },
                  { key: 'placement', label: 'Placement', items: groups.placement },
                  { key: 'contact', label: 'Contact', items: groups.contact },
                ];
                return (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {columns.map((col) => {
                      const ec = ENTITY_COLORS[col.key];
                      return (
                        <div key={col.key} data-testid={`packets-col-${col.key}`}>
                          <div className="flex items-center justify-between mb-2 px-1">
                            <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                              <span className={`inline-block w-2 h-2 rounded-full ${ec.bg}`} />
                              {col.label} Packets
                              <span className="text-xs text-muted-foreground font-normal">({col.items.length})</span>
                            </h3>
                          </div>
                          {col.items.length === 0 ? (
                            <div className="rounded-lg border border-dashed border-slate-200 p-4 text-center text-xs text-muted-foreground">
                              No {col.label.toLowerCase()} packets
                            </div>
                          ) : (
                            <div className="space-y-2">
                              {col.items.map((packet) => {
                                const docCount = packet.template_ids?.length || 0;
                                return (
                                  <div
                                    key={packet.id}
                                    className="group neumorphic rounded-lg px-3 py-2.5 hover:shadow-md transition-shadow"
                                    data-testid={`packet-card-${packet.id}`}
                                  >
                                    <div className="flex items-center gap-2">
                                      <div className="w-7 h-7 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0">
                                        <Package className="w-3.5 h-3.5 text-primary" />
                                      </div>
                                      <h4 className="font-medium text-sm truncate flex-1 min-w-0" title={packet.name}>
                                        {packet.name}
                                      </h4>
                                      <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${ec.bg} ${ec.text} flex-shrink-0 whitespace-nowrap`}>
                                        {ec.label}
                                      </span>
                                      <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                          <Button variant="ghost" size="icon" className="h-6 w-6 flex-shrink-0">
                                            <MoreVertical className="w-3.5 h-3.5" />
                                          </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end">
                                          <DropdownMenuItem onClick={() => handleEditPacket(packet)}>
                                            <Edit2 className="w-4 h-4 mr-2" />
                                            Edit
                                          </DropdownMenuItem>
                                          <DropdownMenuItem
                                            onClick={() => handleDeletePacket(packet.id, packet.name)}
                                            className="text-red-600"
                                          >
                                            <Trash2 className="w-4 h-4 mr-2" />
                                            Delete
                                          </DropdownMenuItem>
                                        </DropdownMenuContent>
                                      </DropdownMenu>
                                    </div>
                                    <div className="flex items-center gap-2 mt-1 text-[11px] text-muted-foreground pl-9 whitespace-nowrap overflow-hidden">
                                      <Files className="w-3 h-3 flex-shrink-0" />
                                      <span>{docCount} doc{docCount !== 1 ? 's' : ''}</span>
                                      <span className="text-slate-300">·</span>
                                      <span>{format(new Date(packet.created_at), 'MMM d, yyyy')}</span>
                                      {packet.templates && packet.templates.length > 0 && (
                                        <>
                                          <span className="text-slate-300">·</span>
                                          <span className="truncate" title={packet.templates.map(t => t.name).join(', ')}>
                                            {packet.templates.slice(0, 3).map(t => t.name).join(', ')}
                                            {packet.templates.length > 3 && ` +${packet.templates.length - 3}`}
                                          </span>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })()
            ) : (
              <div className="neumorphic rounded-xl p-12 text-center">
                <Package className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                <h3 className="font-heading font-semibold text-lg mb-2">No packets yet</h3>
                <p className="text-muted-foreground font-body mb-6 max-w-md mx-auto">
                  Create a packet to group multiple documents together for sending
                </p>
                <Button 
                  onClick={() => setIsPacketOpen(true)}
                  className="btn-primary"
                  data-testid="create-first-packet"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Packet
                </Button>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
